import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import MFASetup from '../components/MFASetup'

export default function SettingsPage() {
  const { profile, user, updateProfile, isCPA } = useAuth()
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' })
  const [profileForm, setProfileForm] = useState({ full_name: profile?.full_name || '' })
  const [pwLoading, setPwLoading] = useState(false)
  const [profileLoading, setProfileLoading] = useState(false)
  const [pwError, setPwError] = useState('')
  const [pwSuccess, setPwSuccess] = useState('')
  const [profileError, setProfileError] = useState('')
  const [profileSuccess, setProfileSuccess] = useState('')

  const setP = k => e => setPasswordForm(f => ({ ...f, [k]: e.target.value }))

  async function handlePasswordChange(e) {
    e.preventDefault()
    setPwError(''); setPwSuccess('')
    if (passwordForm.newPass !== passwordForm.confirm) { setPwError('New passwords do not match'); return }
    if (passwordForm.newPass.length < 8) { setPwError('Password must be at least 8 characters'); return }
    setPwLoading(true)

    // Verify current password by re-authenticating
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: passwordForm.current
    })
    if (signInError) { setPwError('Current password is incorrect'); setPwLoading(false); return }

    // Update password
    const { error } = await supabase.auth.updateUser({ password: passwordForm.newPass })
    if (error) { setPwError(error.message); setPwLoading(false); return }

    setPwSuccess('Password updated successfully!')
    setPasswordForm({ current: '', newPass: '', confirm: '' })
    setTimeout(() => setPwSuccess(''), 4000)
    setPwLoading(false)
  }

  async function handleProfileUpdate(e) {
    e.preventDefault()
    setProfileError(''); setProfileSuccess('')
    setProfileLoading(true)
    const { error } = await updateProfile({ full_name: profileForm.full_name })
    if (error) { setProfileError(error.message); setProfileLoading(false); return }
    setProfileSuccess('Profile updated successfully!')
    setTimeout(() => setProfileSuccess(''), 4000)
    setProfileLoading(false)
  }

  const inputStyle = {
    width: '100%', padding: '11px 14px', borderRadius: 8,
    border: '1.5px solid #DED6CA', fontSize: 14, outline: 'none',
    boxSizing: 'border-box', fontFamily: "'DM Sans', sans-serif"
  }

  const labelStyle = {
    display: 'block', fontSize: 12, fontWeight: 700,
    color: '#202326', marginBottom: 6,
    textTransform: 'uppercase', letterSpacing: 0.5
  }

  return (
    <div style={{ maxWidth: 580 }}>
      {/* Header */}
      <div className="card" style={{ padding: '24px 28px', marginBottom: 24, background: '#202326', border: 'none' }}>
        <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 6 }}>Account Settings</h2>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: 0 }}>
          Manage your profile and security settings.
        </p>
      </div>

      {/* Profile info */}
      <div className="card" style={{ padding: '24px 28px', marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Profile Information</h3>
        <p style={{ fontSize: 13, color: '#62615D', marginBottom: 20 }}>Update your display name.</p>

        <form onSubmit={handleProfileUpdate}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Full Name</label>
            <input
              style={inputStyle}
              value={profileForm.full_name}
              onChange={e => setProfileForm(f => ({ ...f, full_name: e.target.value }))}
              required
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Email Address</label>
            <input style={{ ...inputStyle, background: '#F7F3EB', color: '#62615D' }} value={user?.email || ''} disabled />
            <div style={{ fontSize: 11, color: '#62615D', marginTop: 4 }}>Email cannot be changed. Contact your accountant if needed.</div>
          </div>

          {profileError && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{profileError}</div>}
          {profileSuccess && <div style={{ background: '#D1FAE5', color: '#065F46', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>✓ {profileSuccess}</div>}

          <button type="submit" disabled={profileLoading} style={{
            padding: '10px 24px', background: '#202326', color: '#CBB17B',
            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
            cursor: profileLoading ? 'not-allowed' : 'pointer'
          }}>
            {profileLoading ? 'Saving…' : 'Save Profile'}
          </button>
        </form>
      </div>

      {/* 2FA Section - CPA only */}
      {isCPA && (
        <div className="card" style={{ padding: '24px 28px', marginBottom: 20 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Two-Factor Authentication</h3>
          <p style={{ fontSize: 13, color: '#62615D', marginBottom: 20 }}>
            Protect your firm account with an authenticator app. Required after password login.
          </p>
          <MFASetup />
        </div>
      )}

      {/* Change password */}
      <div className="card" style={{ padding: '24px 28px' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Change Password</h3>
        <p style={{ fontSize: 13, color: '#62615D', marginBottom: 20 }}>Choose a strong password to keep your account secure.</p>

        <form onSubmit={handlePasswordChange}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Current Password</label>
            <input style={inputStyle} type="password" placeholder="Enter current password" value={passwordForm.current} onChange={setP('current')} required />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>New Password</label>
            <input style={inputStyle} type="password" placeholder="Minimum 8 characters" value={passwordForm.newPass} onChange={setP('newPass')} required minLength={8} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Confirm New Password</label>
            <input style={inputStyle} type="password" placeholder="Re-enter new password" value={passwordForm.confirm} onChange={setP('confirm')} required />
          </div>

          {pwError && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{pwError}</div>}
          {pwSuccess && <div style={{ background: '#D1FAE5', color: '#065F46', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>✓ {pwSuccess}</div>}

          <button type="submit" disabled={pwLoading} style={{
            padding: '10px 24px', background: '#202326', color: '#CBB17B',
            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
            cursor: pwLoading ? 'not-allowed' : 'pointer'
          }}>
            {pwLoading ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  )
}
